import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Tooltip } from '@heroui/react';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import {
    buildDataGridSkeletonColumns,
    buildDataGridSkeletonRows,
} from '@/components';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    REGISTER_EMPTY_COPY,
    type AssetRow,
    type RegisterEmptyState,
} from '../../fixedAssetsMeta';
import { AssetStatusChip } from '../assetStatusChip';

type AssetTableProps = {
    ariaLabel: string;
    rows: AssetRow[];
    emptyState: RegisterEmptyState;
    /**
     * The register is still in flight. The table keeps its real column headers
     * and skeletons only the rows — an empty list while loading means "not known
     * yet", never "no assets".
     */
    isLoading?: boolean;
    onSelect: (assetId: string) => void;
    onEdit: (assetId: string) => void;
    onDispose: (assetId: string) => void;
};

const SKELETON_ROW_COUNT = 5;

/**
 * The asset register.
 *
 * A HeroUI Pro `DataGrid`, which renders a real `<table>` underneath — genuine
 * `columnheader`s and a `rowheader` for the name, so the register still reads as
 * a ledger listing. Its accessible name is `aria-label`; DataGrid has no
 * `<caption>` slot. Money columns are right-aligned tabular figures; a nil
 * accumulated figure is an en dash, not `₾ 0.00`.
 *
 * A disposed row is de-emphasised but fully legible — its historical figures are
 * reference, not noise — and offers no Edit or Dispose action, because there is
 * nothing left to do to it. DataGrid exposes no per-row className, so the
 * de-emphasis is applied per cell.
 */
export const AssetTable: React.FC<AssetTableProps> = ({
    ariaLabel,
    rows,
    emptyState,
    isLoading = false,
    onSelect,
    onEdit,
    onDispose,
}) => {
    const { t } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<AssetRow>[]>(
        () => [
            {
                id: 'index',
                header: t('fixedAssets.columns.index'),
                align: 'end',
                width: 56,
                cell: (row) => (
                    <span className="tabular-nums text-muted">{row.index}</span>
                ),
            },
            {
                id: 'name',
                header: t('fixedAssets.columns.name'),
                isRowHeader: true,
                minWidth: 200,
                cell: (row) => (
                    <button
                        className={cn(
                            'cursor-[var(--cursor-interactive)] text-left font-medium underline-offset-2 hover:underline',
                            row.isDisposed ? 'text-muted' : 'text-foreground',
                        )}
                        onClick={() => onSelect(row.id)}
                        type="button"
                        {...getNodeTestIdProp(
                            `fixed-assets-row-open-${row.id}`,
                        )}
                    >
                        {row.name}
                    </button>
                ),
            },
            {
                id: 'category',
                header: t('fixedAssets.columns.category'),
                minWidth: 150,
                cell: (row) => (
                    <span className={cn(row.isDisposed && 'text-muted')}>
                        {row.categoryLabel}
                    </span>
                ),
            },
            {
                id: 'acquisitionDate',
                header: t('fixedAssets.columns.acquisitionDate'),
                minWidth: 140,
                // Money, dates and the life keep their symbol and figure on one
                // line — `₾` wrapping onto its own row reads as a stray glyph.
                cell: (row) => (
                    <span
                        className={cn(
                            'whitespace-nowrap tabular-nums',
                            row.isDisposed && 'text-muted',
                        )}
                    >
                        {row.acquisitionDateText}
                    </span>
                ),
            },
            {
                id: 'cost',
                header: t('fixedAssets.columns.cost'),
                align: 'end',
                minWidth: 130,
                cell: (row) => (
                    <span
                        className={cn(
                            'whitespace-nowrap tabular-nums',
                            row.isDisposed && 'text-muted',
                        )}
                    >
                        {row.costText}
                    </span>
                ),
            },
            {
                id: 'accumulated',
                header: t('fixedAssets.columns.accumulated'),
                align: 'end',
                minWidth: 150,
                cell: (row) => (
                    <span
                        className={cn(
                            'whitespace-nowrap tabular-nums',
                            row.isDisposed && 'text-muted',
                        )}
                    >
                        {row.accumulatedText}
                    </span>
                ),
            },
            {
                id: 'netBookValue',
                header: t('fixedAssets.columns.netBookValue'),
                align: 'end',
                minWidth: 140,
                cell: (row) => (
                    <span
                        className={cn(
                            'whitespace-nowrap font-medium tabular-nums',
                            row.isDisposed ? 'text-muted' : 'text-foreground',
                        )}
                    >
                        {row.netBookValueText}
                    </span>
                ),
            },
            {
                id: 'method',
                header: t('fixedAssets.columns.method'),
                minWidth: 140,
                cell: (row) => (
                    <span
                        className={cn(
                            'whitespace-nowrap',
                            row.isDisposed && 'text-muted',
                        )}
                    >
                        {t(row.methodLabelKey)}
                    </span>
                ),
            },
            {
                id: 'usefulLife',
                header: t('fixedAssets.columns.usefulLife'),
                align: 'end',
                minWidth: 120,
                cell: (row) => (
                    <span
                        className={cn(
                            'whitespace-nowrap tabular-nums',
                            row.isDisposed && 'text-muted',
                        )}
                    >
                        {t('fixedAssets.lifeMonths', {
                            count: row.usefulLifeMonths,
                        })}
                    </span>
                ),
            },
            {
                id: 'status',
                header: t('fixedAssets.columns.status'),
                minWidth: 120,
                cell: (row) => (
                    <AssetStatusChip slot={row.id} status={row.status} />
                ),
            },
            {
                id: 'actions',
                header: (
                    <span className="sr-only">
                        {t('fixedAssets.columns.actions')}
                    </span>
                ),
                align: 'end',
                width: 104,
                // The accessible names live in visually hidden spans, not
                // `aria-label`: HeroUI's Tooltip wrapper drops an `aria-label`
                // set on its trigger Button (verified in the DOM), leaving an
                // icon-only trigger nameless. Text content always survives, and
                // naming the row keeps the two buttons distinct in a screen
                // reader's element list.
                cell: (row) =>
                    row.isDisposed ? null : (
                        <span
                            className="flex items-center justify-end gap-1"
                            data-row-actions=""
                        >
                            <Tooltip delay={0}>
                                <Button
                                    isIconOnly
                                    onPress={() => onEdit(row.id)}
                                    size="sm"
                                    variant="ghost"
                                    {...getNodeTestIdProp(
                                        `fixed-assets-row-edit-${row.id}`,
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        icon="gravity-ui:pencil"
                                    />
                                    <span className="sr-only">
                                        {t('fixedAssets.actions.editNamed', {
                                            name: row.name,
                                        })}
                                    </span>
                                </Button>
                                <Tooltip.Content>
                                    <p>{t('fixedAssets.actions.edit')}</p>
                                </Tooltip.Content>
                            </Tooltip>
                            <Tooltip delay={0}>
                                <Button
                                    isIconOnly
                                    onPress={() => onDispose(row.id)}
                                    size="sm"
                                    variant="ghost"
                                    {...getNodeTestIdProp(
                                        `fixed-assets-row-dispose-${row.id}`,
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        icon="gravity-ui:box-arrow-up"
                                    />
                                    <span className="sr-only">
                                        {t('fixedAssets.actions.disposeNamed', {
                                            name: row.name,
                                        })}
                                    </span>
                                </Button>
                                <Tooltip.Content>
                                    <p>{t('fixedAssets.actions.dispose')}</p>
                                </Tooltip.Content>
                            </Tooltip>
                        </span>
                    ),
            },
        ],
        [onDispose, onEdit, onSelect, t],
    );

    if (!isLoading && emptyState !== 'NONE') {
        const copy = REGISTER_EMPTY_COPY[emptyState];
        return (
            <div
                className="flex flex-col items-center gap-2 rounded-2xl border border-separator bg-surface px-6 py-14 text-center"
                {...getNodeTestIdProp(`fixed-assets-empty-${emptyState}`)}
            >
                <Icon
                    aria-hidden
                    className="size-8 text-muted"
                    icon={copy.icon}
                />
                <span className="text-sm font-medium text-foreground">
                    {t(copy.titleKey)}
                </span>
                <span className="max-w-sm text-sm text-muted">
                    {t(copy.bodyKey)}
                </span>
            </div>
        );
    }

    // `contents` wrapper: `DataGrid` forwards no unknown props, so the
    // register's testid has nowhere to land on the grid itself.
    return (
        <div className="contents" {...getNodeTestIdProp('fixed-assets-table')}>
            {isLoading ? (
                <DataGrid<{ id: string }>
                    aria-label={ariaLabel}
                    className="ledger-data-grid"
                    columns={buildDataGridSkeletonColumns(columns)}
                    contentClassName="min-w-[1080px]"
                    data={buildDataGridSkeletonRows(
                        SKELETON_ROW_COUNT,
                        'fixed-assets',
                    )}
                    getRowId={(row) => row.id}
                />
            ) : (
                <DataGrid<AssetRow>
                    aria-label={ariaLabel}
                    className="ledger-data-grid"
                    columns={columns}
                    contentClassName="min-w-[1080px]"
                    data={rows}
                    getRowId={(row) => row.id}
                />
            )}
        </div>
    );
};
